import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterIcon from "@/assets/icons/filter.svg?react";
import { DownloadIcon } from "@/components/atomic/DownloadIcon";
import { Input } from "@/components/atomic/Input";
import { FilterInfo } from "@/components/atomic/voc-filter/FilterInfo";
import VocsView from "@/components/ui/VocsView";
import { useVocSearch } from "@/hooks/useVocSearch";
import { cn } from "@/lib/utils";
import Button from "./Button";
import { VocSearchFilter } from "./VocSearchFilter";

export const VocSearch = () => {
  const { t } = useTranslation("voiceek_ui");
  const {
    keyword,
    setKeyword,
    filters,
    updateFilters,
    clearFilters,
    result,
    isSearching,
    isDownloading,
    handleSearch,
    handleDownload,
  } = useVocSearch();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const vocSearchContainerRef = useRef<HTMLDivElement>(null);
  const vocSearchFilterRef = useRef<HTMLDivElement>(null);
  const vocSearchInputRef = useRef<HTMLDivElement>(null);
  const vocSearchFilterButtonRef = useRef<HTMLButtonElement>(null);

  // フィルター外をクリックしたら閉じる
  useEffect(() => {
    if (!isFilterOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // VocSearchContainer内のクリックでない場合は何もしない
      if (!vocSearchContainerRef.current?.contains(target)) {
        return;
      }

      // VocSearchFilter内、VocSearchInput内、またはフィルターボタンのクリックは無視
      if (
        vocSearchFilterRef.current?.contains(target)
        || vocSearchInputRef.current?.contains(target)
        || vocSearchFilterButtonRef.current?.contains(target)
      ) {
        return;
      }

      // VocSearchContainer内かつFilter/Input外がクリックされたので閉じる
      setIsFilterOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isFilterOpen]);

  return (
    <div
      ref={vocSearchContainerRef}
      className="flex-1 flex flex-col min-h-0 rounded-b-[inherit] bg-white"
    >
      <div className="max-w-[1140px] w-full mx-auto flex flex-col gap-2 p-8 min-h-0 flex-1">
        {/* 検索入力エリア */}
        <div
          ref={vocSearchInputRef}
          className="flex items-center border border-[#6888E1] rounded-[5px] bg-white h-[60px] px-6 focus-within:shadow-[0_0_5px_0_#6888E1]"
        >
          <Input
            type="text"
            placeholder={t("search.placeholder")}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="flex-1 border-none h-7 shadow-none focus:ring-0 text-base placeholder:text-[#B7B7B7] px-0 caret-[#6888E1]"
            disabled={isSearching}
          />
          <Button
            variant="secondary"
            onClick={() => {
              handleSearch();
              setIsFilterOpen(false);
            }}
            disabled={!keyword || isSearching}
            className="w-25 rounded-full shadow-[0px_2px_#3C569D]"
          >
            {t("search.button")}
          </Button>
        </div>

        {/* ダウンロードリンク */}
        <div className="w-full flex justify-between">
          <button
            ref={vocSearchFilterButtonRef}
            className={cn(
              "flex gap-1 pl-1 pr-2 h-[34px] items-center hover:bg-gray-100 rounded-[5px] hover:cursor-pointer",
              isFilterOpen && "bg-[#E1EAFF]",
            )}
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            aria-label="Toggle filter"
            data-testid="voc-search-filter-button"
          >
            <FilterIcon className="h-[26px] w-[26px] fill-[#6888E1] " />
            <span className="text-[14px] text-[#6888E1] font-semibold">{t("vocFilter.filter")}</span>
          </button>
          {result?.vocs && result.vocs.length > 0 && (
            <button
              onClick={handleDownload}
              disabled={isDownloading || !keyword}
              className="flex items-center gap-[6px] text-[#6888e1] hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="w-[26px] h-[26px] flex items-center justify-center">
                <DownloadIcon isLoading={isDownloading} className="w-[17px] h-[17px]" />
              </span>
              <span className="font-semibold text-[14px] leading-[14px] text-nowrap whitespace-pre shrink-0">
                {t("search.download")}
              </span>
            </button>
          )}
        </div>
        <div className="relative">
          {isFilterOpen && (
            <div ref={vocSearchFilterRef} className="absolute z-50 w-full">
              <VocSearchFilter
                periodFilter={filters.periodFilter}
                categories={filters.categories}
                attributeFilters={filters.attributeFilters}
                tagIds={filters.tagIds}
                onUpdatePeriodFilter={updateFilters.updatePeriodFilter}
                onUpdateCategories={updateFilters.updateCategories}
                onUpdateAttributeFilter={updateFilters.updateAttributeFilter}
                onToggleTagId={updateFilters.toggleTagId}
                onClearFilters={clearFilters}
              />
            </div>
          )}
        </div>

        {/* 検索結果 */}
        {result && (
          <div className="w-full min-h-0 flex-1">
            {result.vocs.length > 0
              ? (
                <VocsView
                  vocs={result.vocs}
                  title={result.total ? t("search.resultsWithCount", { count: result.total }) : t("search.results")}
                  innerScrollable={true}
                  className="h-full"
                  headerContent={
                    <FilterInfo
                      vocPeriodStart={result.startDate}
                      vocPeriodEnd={result.endDate}
                      categories={result.categories.length > 0 ? result.categories : undefined}
                      attributeFilters={result.attributeFilters}
                      tagIds={result.tagIds}
                    />
                  }
                />
              )
              : (
                !isSearching && (
                  <>
                    <FilterInfo
                      vocPeriodStart={result.startDate}
                      vocPeriodEnd={result.endDate}
                      categories={result.categories.length > 0 ? result.categories : undefined}
                      attributeFilters={result.attributeFilters}
                      tagIds={result.tagIds}
                      defaultOpen={true}
                    />
                    <div className="text-center text-gray-500 mt-8">
                      {t("search.noResults")}
                    </div>
                  </>
                )
              )}
          </div>
        )}
      </div>
    </div>
  );
};
